import { ShellHeader } from '@/app/shell-header';
import { GROUPS, RESOURCES } from '@/data/groups';
import { RECORDS, type DataRecord, type RecordStatus } from '@/data/records';
import { dayFromToday, TODAY } from '@/data/seed';
import { formatDate } from '@/lib/format';
import { cn, PageHeader } from '@mgrvtechgroup/admin-ds';
import { addDays, differenceInCalendarDays, format, parseISO } from 'date-fns';
import { ko } from 'date-fns/locale';
import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';
import { clipToWindow, recordsByResource } from './timeline-model';

// 보이는 기간 — 지난 2주 ~ 앞으로 3개월
const WINDOW_START = dayFromToday(-14);
const WINDOW_END = dayFromToday(90);
const WINDOW_DAYS = differenceInCalendarDays(parseISO(WINDOW_END), parseISO(WINDOW_START)) + 1;
const TODAY_INDEX = differenceInCalendarDays(TODAY, parseISO(WINDOW_START));
const DAYS = Array.from({ length: WINDOW_DAYS }, (_, i) => addDays(parseISO(WINDOW_START), i));

const dayIndex = (date: string) => differenceInCalendarDays(parseISO(date), parseISO(WINDOW_START));

const PX_PER_DAY = 14;
const TRACK_WIDTH = WINDOW_DAYS * PX_PER_DAY;

/** 행 높이는 admin-ds D17(표 행 40px 고정)을 따른다. */
const ROW_HEIGHT = 40;

const RESOURCE_COLUMN_WIDTH = 120;

/**
 * 상태 색은 의미가 고정된다. 색만으로 구분하지 않도록 블록에는 항상 이름을 쓰고,
 * 미확정(검토 대기)은 점선으로 모양까지 다르게 한다.
 */
const STATUS_BLOCK: Record<RecordStatus, string> = {
  '검토 대기': 'border-dashed border-muted-foreground/60 bg-background text-muted-foreground',
  예정: 'border-info-border bg-info-subtle text-info-text',
  '진행 중': 'border-success-border bg-success-subtle text-success-text',
  '종료 임박': 'border-warning-border bg-warning-subtle text-warning-text',
  완료: 'border-border bg-muted text-muted-foreground',
  취소: 'border-destructive-border bg-destructive-subtle text-destructive-text',
};
const LEGEND = Object.keys(STATUS_BLOCK) as RecordStatus[];

/**
 * 타임라인 뷰 패턴 — 리소스(행) × 날짜(열) 위에 기간 블록을 놓는다. 오늘 선·범례·가로 스크롤을 보여 주고,
 * 블록을 누르면 그 항목의 상세 페이지로 간다.
 */
export function TimelinePage() {
  const scrollerRef = useRef<HTMLDivElement>(null);
  // 리소스 코드 순으로 나열한다(A-01, A-02 …).
  const resources = [...RESOURCES].sort((a, b) =>
    a.code.localeCompare(b.code, 'ko', { numeric: true }),
  );
  const lanes = useMemo(() => recordsByResource(RECORDS), []);

  // 오늘을 화면 왼쪽 1/4 지점에 둔다 — 지난 기록보다 다가올 일정을 더 넓게 보여 준다.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (scroller) {
      scroller.scrollLeft =
        TODAY_INDEX * PX_PER_DAY - (scroller.clientWidth - RESOURCE_COLUMN_WIDTH) / 4;
    }
  }, []);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <ShellHeader>
        <PageHeader
          title="타임라인 뷰"
          description="리소스 × 날짜 위에 기간 블록을 놓고 일정을 보는 페이지입니다. 블록을 누르면 상세로 이동합니다."
        />
      </ShellHeader>

      <ul
        className="flex flex-wrap items-center gap-x-3 gap-y-1 px-[var(--layout-gutter)] py-3"
        aria-label="상태 범례"
      >
        {LEGEND.map((status) => (
          <li key={status} className="caption text-muted-foreground flex items-center gap-1.5">
            <span
              className={cn('inline-block h-3 w-4 rounded-xs border', STATUS_BLOCK[status])}
              aria-hidden
            />
            {status}
          </li>
        ))}
      </ul>

      <div ref={scrollerRef} className="relative min-h-0 flex-1 overflow-auto border-t">
        <div className="relative" style={{ width: RESOURCE_COLUMN_WIDTH + TRACK_WIDTH }}>
          <TimelineHeader />

          {/* 오늘 세로선 — 헤더 아래 모든 행을 가로지른다 */}
          <div
            aria-hidden
            className="bg-foreground/60 pointer-events-none absolute top-0 bottom-0 z-[5] w-px"
            style={{ left: RESOURCE_COLUMN_WIDTH + TODAY_INDEX * PX_PER_DAY + PX_PER_DAY / 2 }}
          />

          {GROUPS.map((group) => (
            <div key={group.id}>
              <div className="bg-muted/40 flex border-b">
                <div
                  className="bg-muted label sticky left-0 z-10 flex h-7 shrink-0 items-center border-r px-3"
                  style={{ width: RESOURCE_COLUMN_WIDTH }}
                >
                  {group.name}
                </div>
                <div style={{ width: TRACK_WIDTH }} />
              </div>
              {resources
                .filter((resource) => resource.groupId === group.id)
                .map((resource) => (
                  <TimelineRow
                    key={resource.id}
                    label={resource.code}
                    records={lanes.get(resource.id)}
                  />
                ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** 월 행 + 월요일 날짜 행. 위쪽에 붙고, 리소스 열 머리는 왼쪽에도 붙는다. */
function TimelineHeader() {
  const months = useMemo(() => {
    const spans: { key: string; label: string; start: number; length: number }[] = [];
    DAYS.forEach((day, index) => {
      const key = format(day, 'yyyy-MM');
      const last = spans.at(-1);
      if (last?.key === key) {
        last.length += 1;
      } else {
        spans.push({ key, label: format(day, 'yyyy년 M월'), start: index, length: 1 });
      }
    });
    return spans;
  }, []);

  return (
    <div className="bg-background sticky top-0 z-20 flex border-b">
      <div
        className="bg-background caption text-muted-foreground sticky left-0 z-10 flex shrink-0 items-end border-r px-3 pb-1.5 font-medium"
        style={{ width: RESOURCE_COLUMN_WIDTH }}
      >
        리소스
      </div>
      <div className="relative" style={{ width: TRACK_WIDTH, height: 52 }}>
        {months.map((month) => (
          <div
            key={month.key}
            className="caption absolute top-0 flex h-7 items-center truncate border-l px-2 font-medium"
            style={{ left: month.start * PX_PER_DAY, width: month.length * PX_PER_DAY }}
          >
            {month.label}
          </div>
        ))}
        {DAYS.map((day, index) => {
          const isToday = index === TODAY_INDEX;
          // 월요일에만 날짜를 적는다 — 14px 칸에 매일 쓰면 숫자가 겹친다. 오늘 표시와 겹치는 주 라벨은 숨긴다.
          const nearToday = Math.abs(index - TODAY_INDEX) < Math.ceil(36 / PX_PER_DAY);
          if (!isToday && (day.getDay() !== 1 || nearToday)) {
            return null;
          }
          return (
            <div
              key={day.toISOString()}
              className={cn(
                'caption absolute top-7 flex h-6 items-center justify-center tabular-nums',
                isToday && 'bg-foreground text-background rounded-sm font-medium',
              )}
              style={{ left: index * PX_PER_DAY, width: PX_PER_DAY * 3 }}
              title={format(day, 'yyyy.MM.dd (EEE)', { locale: ko })}
            >
              {format(day, 'M/d')}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TimelineRow({ label, records = [] }: { label: string; records?: DataRecord[] }) {
  return (
    <div className="flex border-b">
      <div
        className="bg-background body sticky left-0 z-10 flex shrink-0 items-center border-r px-3 tabular-nums"
        style={{ width: RESOURCE_COLUMN_WIDTH, height: ROW_HEIGHT }}
      >
        {label}
      </div>
      <div className="relative" style={{ width: TRACK_WIDTH, height: ROW_HEIGHT }}>
        {records.map((record) => (
          <TimelineBlock key={record.id} record={record} />
        ))}
      </div>
    </div>
  );
}

function TimelineBlock({ record }: { record: DataRecord }) {
  const navigate = useNavigate();
  const placement = clipToWindow(
    { start: record.startDate, end: record.endDate },
    WINDOW_START,
    WINDOW_END,
    dayIndex,
  );
  if (!placement) {
    return null;
  }
  const description = `${record.title} · ${record.status} · ${formatDate(record.startDate)} ~ ${formatDate(record.endDate)}`;

  return (
    <button
      type="button"
      title={description}
      aria-label={description}
      onClick={() => {
        void navigate(`/dev/list/${record.id}`);
      }}
      className={cn(
        // overflow-clip(hidden 아님) — 스크롤 컨테이너를 만들지 않아 안쪽 이름이 바깥 가로 스크롤 기준으로 sticky 된다.
        'caption absolute flex items-center overflow-clip rounded-sm border px-1.5 text-left font-medium whitespace-nowrap',
        'focus-visible:ring-ring hover:brightness-95 focus-visible:ring-2 focus-visible:outline-none',
        STATUS_BLOCK[record.status],
      )}
      style={{
        left: placement.offset * PX_PER_DAY + 1,
        width: Math.max(placement.length * PX_PER_DAY - 2, 4),
        top: 4,
        height: ROW_HEIGHT - 8,
      }}
    >
      {/* 왼쪽이 화면 밖으로 나간 긴 항목도 이름이 보이도록 고정 리소스 열 바로 옆에 붙인다. */}
      <span className="sticky truncate" style={{ left: RESOURCE_COLUMN_WIDTH + 6 }}>
        {record.title}
      </span>
    </button>
  );
}
